import { Select as SelectPrimitive } from "radix-ui";
import { forwardRef, type PropsWithChildren, useRef } from "react";
import { customColors } from "tailwind.config";

import DropDownIcon from "$/lib/icons/DropDownIcon";
import { WarningIcon } from "$/lib/icons/WarningIcon";
import { cn } from "$/lib/utils/functions/misc.functions";

export type SelectInputProps = {
  placeholder?: string;
  isErrored?: boolean;
  errorMessage?: string;
  defaultValue?: string;
  className?: string;
  highlightBorder?: boolean;
  onSelect?: (value: string) => void;
  iconClassName?: string;
  contentClassName?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  readOnly?: boolean; // visually identical to normal but non-interactive
};

export const SelectInput = forwardRef<
  HTMLButtonElement,
  PropsWithChildren<SelectInputProps>
>(
  (
    { children, iconClassName, contentClassName, icon, ...props },
    forwardedRef,
  ) => {
    const containerRef = useRef<HTMLDivElement | null>(null);

    return (
      <div className="group w-full" ref={containerRef}>
        <SelectPrimitive.Root
          defaultValue={props.defaultValue}
          onValueChange={(value) => {
            props.onSelect?.(value);
            setTimeout(() => {
            containerRef.current?.querySelector("button")?.blur();
            }, 0);
            }}
          disabled={props.disabled}
          open={props.readOnly ? false : undefined}
          // keep component uncontrolled; readOnly handled via overlay & tabIndex
        >
          <SelectPrimitive.Trigger
            aria-invalid={props.isErrored}
            className={cn(
              "relative inline-flex h-12 min-h-12 w-full items-center justify-between gap-4 rounded-lg border bg-white px-4 py-2.5 text-sm font-medium leading-none text-black outline-none ring-1 ring-transparent duration-150 hover:border-inputFocusBorder hover:shadow-sm disabled:cursor-not-allowed disabled:opacity-50 group-focus-within:border-inputFocusBorder data-[state='open']:border-inputFocusBorder data-[placeholder]:text-textInfo aria-invalid:border-error aria-invalid:group-focus-within:border-error aria-invalid:data-[state='open']:border-error",
              props.highlightBorder && "border-error",
              props.className,
            )}
            disabled={props.disabled}
            // remove from tab order if readOnly (but still show normal styles)
            tabIndex={props.readOnly ? -1 : undefined}
            data-readonly={props.readOnly ? "true" : undefined}
            ref={forwardedRef}
          >
            <SelectPrimitive.Value placeholder={props.placeholder} />
            <SelectPrimitive.Icon>
              {icon ?? <DropDownIcon className={cn("size-5", iconClassName)} />}
            </SelectPrimitive.Icon>
          </SelectPrimitive.Trigger>
          <SelectPrimitive.Content
            position="popper"
            align="center"
            className={cn(
              "z-20 mt-px rounded-b-md border border-t-0 border-selectInputFocusBorder bg-white px-2 py-2 shadow-lg",
              contentClassName,
            )}
            style={{
              width: "calc(var(--radix-select-trigger-width) - 2rem)",
            }}
          >
            <SelectPrimitive.Viewport>{children}</SelectPrimitive.Viewport>
          </SelectPrimitive.Content>
        </SelectPrimitive.Root>
        {props.isErrored && !!props.errorMessage && (
          <p className="mt-1 flex items-center gap-1 text-xs text-error">
            <WarningIcon
              color={customColors.error.DEFAULT}
              className="size-4"
            />
            {props.errorMessage}
          </p>
        )}
      </div>
    );
  },
);
SelectInput.displayName = "SelectInput";

export const SelectInputOption = forwardRef<
  HTMLDivElement,
  PropsWithChildren<SelectPrimitive.SelectItemProps>
>(({ children, ...props }, forwardedRef) => {
  return (
    <SelectPrimitive.Item
      {...props}
      ref={forwardedRef}
      className={cn(
        "group border-b border-border py-0.5 text-sm outline-none duration-75 first:pt-0 last:border-none last:pb-0",
        props.className,
      )}
    >
      <div className="rounded-md px-2 py-1 text-textInfo group-data-[highlighted]:bg-primary/5 group-data-[state='checked']:bg-primary/5 group-data-[highlighted]:text-textColor group-data-[state='checked']:text-textColor">
        <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
      </div>
    </SelectPrimitive.Item>
  );
});

SelectInputOption.displayName = "SelectInputOption";
